import React from 'react';
import { Users, CheckCircle2, Clock, XCircle, Mail, MessageSquare, MapPin, UtensilsCrossed, Sparkles, Send, Star, Heart } from 'lucide-react';

/* Tiny product mockups that mirror the real dashboard's visual language.
   Used inside <BrowserFrame> on the landing page's hero + feature blocks. */

const WINE = '#7B2C3B';
const CREAM = '#FBF7F2';
const OCHRE = '#B8896C';
const SAGE = '#4A7A4A';
const STONE = '#9A8A7E';

// ---------- 1. Wedding Dashboard (used as hero) ----------
export function MockWeddingDashboard({ compact = false }) {
  const donut = 'conic-gradient(#4A7A4A 0 43%, #7B2C3B 0 71%, #B8896C 0 86%, #E8C267 0 100%)';
  return (
    <div className={`${compact ? 'p-3' : 'p-5'} space-y-3`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E]">Wedding dashboard</p>
          <p className="font-serif text-lg text-[#2A2A2A] leading-tight">Brent &amp; Christina</p>
        </div>
        <div className="text-[10px] px-2 py-0.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">Nov 7 · 44 days</div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Invited', value: '148', bg: WINE, text: '#fff' },
          { label: 'Attending', value: '112', bg: SAGE, text: '#fff' },
          { label: 'Awaiting', value: '28', bg: '#E8C267', text: '#3A2A00' },
          { label: 'Declined', value: '8', bg: STONE, text: '#fff' },
        ].map((k) => (
          <div key={k.label} className="rounded-lg p-2" style={{ background: k.bg, color: k.text }}>
            <p className="text-[9px] uppercase opacity-80 tracking-wider">{k.label}</p>
            <p className="font-serif text-xl leading-none mt-0.5">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-5 gap-2">
        <div className="col-span-2 rounded-lg border border-[#EFE3D8] p-3 bg-white">
          <p className="text-[10px] font-medium text-[#3A3A3A] mb-2">RSVP breakdown</p>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full" style={{ background: donut }}>
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-9 h-9 rounded-full bg-white" />
              </div>
            </div>
            <div className="text-[9px] space-y-0.5 text-[#4A4A4A]">
              <p><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: SAGE }} />Yes 64</p>
              <p><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: WINE }} />Pending 42</p>
              <p><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: OCHRE }} />No 22</p>
              <p><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#E8C267' }} />Kids 20</p>
            </div>
          </div>
        </div>

        <div className="col-span-3 rounded-lg border border-[#EFE3D8] p-3 bg-white">
          <p className="text-[10px] font-medium text-[#3A3A3A] mb-2">Email delivery</p>
          <div className="flex items-end gap-1 h-14">
            {[85, 72, 68, 55, 48, 42, 38].map((h, i) => (
              <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: `linear-gradient(180deg,${WINE},#B8896C)` }} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1 mt-1 text-center text-[8px] text-[#6A6A6A]">
            <div>Sent 141</div>
            <div className="text-[#7B2C3B]">Opened 96</div>
            <div>Replied 78</div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#F4C1C1] bg-[#FDECEC] p-2 flex items-start gap-2">
        <div className="text-[#7B2C3B] mt-0.5"><Send className="w-3 h-3" /></div>
        <div className="text-[9px] text-[#4A2020] leading-tight">
          <strong>18 guests</strong> opened the invite but haven&apos;t replied. <span className="underline">Send nudge SMS →</span>
        </div>
      </div>
    </div>
  );
}

// ---------- 2. Email invitation ----------
export function MockEmailInvitation() {
  return (
    <div className="p-3">
      <div className="rounded-lg overflow-hidden border border-[#EFE3D8] bg-[#FBF7F2]">
        <div className="p-4 text-center border-b border-[#EFE3D8]">
          <p className="text-[8px] uppercase tracking-[0.3em] text-[#B8896C]">You&apos;re invited</p>
          <p className="font-serif text-2xl text-[#2A2A2A] mt-1 leading-tight">Brent<br /><span className="italic text-[#7B2C3B]">&amp;</span> Christina</p>
          <div className="mx-auto mt-2 w-16 h-16 rounded-full bg-gradient-to-br from-[#F4D6CE] to-[#B8896C]" />
          <p className="text-[9px] text-[#4A4A4A] mt-2 leading-tight">Saturday · November 7, 2026 · 4:30 PM<br />The Five Crowns · Corona del Mar, CA</p>
        </div>
        <div className="p-3 text-center">
          <a className="inline-block px-5 py-1.5 rounded-full bg-[#7B2C3B] text-white text-[9px] font-medium" href="#">RSVP by Oct 15</a>
          <p className="text-[8px] text-[#6A6A6A] mt-1">Kindly reply at your earliest convenience</p>
        </div>
        <div className="border-t border-[#EFE3D8] p-2 text-center">
          <p className="text-[8px] uppercase tracking-[0.25em] text-[#B8896C]">Everything else</p>
          <p className="text-[8px] text-[#4A4A4A] mt-0.5">Hotels, travel &amp; day-of schedule → <span className="underline text-[#7B2C3B]">See details</span></p>
        </div>
      </div>
    </div>
  );
}

// ---------- 3. RSVP page ----------
export function MockRsvpPage() {
  return (
    <div className="p-3 space-y-2">
      <div className="text-center">
        <p className="text-[8px] uppercase tracking-[0.3em] text-[#B8896C]">RSVP</p>
        <p className="font-serif text-lg text-[#2A2A2A]">Brent &amp; Christina</p>
        <p className="text-[9px] text-[#6A6A6A]">Saturday, November 7, 2026</p>
        <div className="mt-2 mx-auto w-full h-16 rounded-lg bg-[url('https://images.unsplash.com/photo-1519741497674-611481863552?w=600&auto=format')] bg-cover bg-center" />
      </div>
      <div className="rounded-lg border border-[#EFE3D8] p-3 bg-white space-y-2">
        <p className="text-[9px] text-[#2A2A2A] font-medium">Hello, Sophia!</p>
        <div className="grid grid-cols-3 gap-1">
          <div className="rounded-full py-1.5 text-[9px] text-center bg-[#7B2C3B] text-white">Yes ✓</div>
          <div className="rounded-full py-1.5 text-[9px] text-center border border-[#EFE3D8] text-[#6A6A6A]">Maybe</div>
          <div className="rounded-full py-1.5 text-[9px] text-center border border-[#EFE3D8] text-[#6A6A6A]">No</div>
        </div>
        <div className="rounded-lg border border-[#EFE3D8] p-2">
          <p className="text-[8px] text-[#2A2A2A]">Bringing a plus one?</p>
          <div className="grid grid-cols-2 gap-1 mt-1">
            <div className="rounded-full py-1 text-[8px] text-center bg-[#7B2C3B] text-white">Yes, Marc</div>
            <div className="rounded-full py-1 text-[8px] text-center border border-[#EFE3D8]">No, just me</div>
          </div>
        </div>
        <div className="rounded-md border border-[#EFE3D8] px-2 py-1 text-[8px] text-[#9A8A7E]">Meal preference · Chicken ▾</div>
        <div className="rounded-md border border-[#EFE3D8] px-2 py-1 text-[8px] text-[#9A8A7E]">you@example.com</div>
        <div className="rounded-full bg-[#7B2C3B] text-white text-[9px] text-center py-1.5 font-medium">Send my reply</div>
      </div>
    </div>
  );
}

// ---------- 4. Guests list ----------
export function MockGuestsList() {
  const rows = [
    { name: 'Sophia Marchetti', tag: null, rsvp: 'Yes', side: 'Bride', meal: 'Chicken' },
    { name: 'Emma Clarke', tag: null, rsvp: 'Yes', side: 'Bride', meal: 'Vegetarian' },
    { name: 'Cindy Boehm', tag: 'NEEDS EMAIL', rsvp: 'Pending', side: 'Groom', meal: '—' },
    { name: 'Liam Reed', tag: null, rsvp: 'Yes', side: 'Groom', meal: 'Fish' },
    { name: 'Jamie Fox', tag: 'NEEDS EMAIL', rsvp: 'Pending', side: 'Bride', meal: '—' },
    { name: 'Hannah Vogel', tag: null, rsvp: 'No', side: 'Both', meal: '—' },
  ];
  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-serif text-base text-[#2A2A2A]">Guest list</p>
        <div className="text-[9px] px-2 py-0.5 rounded-full bg-[#7B2C3B] text-white inline-flex items-center gap-1">
          <Mail className="w-2.5 h-2.5" /> Needs email <span className="ml-1 bg-white/25 rounded-full px-1">2</span>
        </div>
      </div>
      <div className="flex gap-1">
        {['All', 'Yes', 'Pending', 'No'].map((f, i) => (
          <span key={f} className={`text-[9px] px-2 py-0.5 rounded-full border ${i === 0 ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#EFE3D8] text-[#3A3A3A]'}`}>{f}</span>
        ))}
      </div>
      <div className="rounded-lg border border-[#EFE3D8] bg-white overflow-hidden">
        {rows.map((g, i) => (
          <div key={i} className={`grid grid-cols-[1fr_50px_60px_60px] items-center gap-2 px-2 py-1.5 text-[9px] ${i < rows.length - 1 ? 'border-b border-[#EFE3D8]' : ''}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className={`w-1.5 h-1.5 rounded-full ${g.rsvp === 'Yes' ? 'bg-[#4A7A4A]' : g.rsvp === 'No' ? 'bg-[#9A8A7E]' : 'bg-[#E8C267]'}`} />
              <span className="text-[#2A2A2A] truncate">{g.name}</span>
              {g.tag && <span className="text-[7px] uppercase px-1 py-0.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] border border-[#B8896C]">{g.tag}</span>}
            </div>
            <span className="text-[#6A6A6A]">{g.side}</span>
            <span className={`font-medium ${g.rsvp === 'Yes' ? 'text-[#4A7A4A]' : g.rsvp === 'No' ? 'text-[#7A5555]' : 'text-[#B8896C]'}`}>{g.rsvp}</span>
            <span className="text-[#6A6A6A]">{g.meal}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- 5. AI Wedding Friend ----------
export function MockAiFriend() {
  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F4D6CE] to-[#B8896C] flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-[#7B2C3B]" />
        </div>
        <div>
          <p className="font-serif text-sm text-[#2A2A2A] leading-tight">Your wedding friend</p>
          <p className="text-[8px] text-[#6A6A6A]">Powered by GPT-5 · knows your dates &amp; budget</p>
        </div>
      </div>
      <div className="rounded-lg bg-[#F4E4DA] p-2">
        <p className="text-[9px] text-[#7B2C3B] font-medium mb-0.5">wedding friend</p>
        <p className="text-[9px] text-[#2A2A2A] leading-snug">For 120 guests I&apos;d budget <strong>$2,200–$3,400 for flowers</strong> — that&apos;s ~$18–28 per guest with a few splurge moments (arch, sweetheart table, bouquets).</p>
      </div>
      <div className="rounded-lg bg-white border border-[#EFE3D8] p-2 ml-6">
        <p className="text-[9px] text-[#6A6A6A] font-medium mb-0.5">you</p>
        <p className="text-[9px] text-[#3A3A3A]">And what if I only want florals at the ceremony?</p>
      </div>
      <div className="flex flex-wrap gap-1">
        {['Vendor questions', 'Save-the-date copy', "'No kids' wording"].map((c) => (
          <span key={c} className="text-[9px] px-2 py-0.5 rounded-full border border-[#EFE3D8] text-[#3A3A3A] bg-white">{c}</span>
        ))}
      </div>
      <div className="rounded-full border border-[#EFE3D8] bg-white flex items-center gap-2 px-2 py-1.5">
        <span className="text-[9px] text-[#9A8A7E] flex-1">Ask me anything about your wedding…</span>
        <div className="w-5 h-5 rounded-full bg-[#7B2C3B] flex items-center justify-center"><Send className="w-2.5 h-2.5 text-white" /></div>
      </div>
    </div>
  );
}

// ---------- 6. Seating ----------
export function MockSeating() {
  const tables = [
    { label: 'Sweetheart', filled: 2, seats: 2, x: 50, y: 15 },
    { label: 'Family', filled: 8, seats: 8, x: 20, y: 55 },
    { label: 'Friends', filled: 7, seats: 8, x: 50, y: 60 },
    { label: 'Work', filled: 5, seats: 6, x: 80, y: 55 },
  ];
  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-serif text-base text-[#2A2A2A]">Seating plan</p>
        <span className="text-[9px] text-[#4A7A4A] bg-[#EAF2E5] px-2 py-0.5 rounded-full">22 of 24 seated</span>
      </div>
      <div className="relative rounded-lg border border-[#EFE3D8] bg-white h-40">
        {tables.map((t) => (
          <div key={t.label} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${t.x}%`, top: `${t.y}%` }}>
            <div className={`w-14 h-14 rounded-full flex items-center justify-center border-2 ${t.filled === t.seats ? 'border-[#4A7A4A] bg-[#EAF2E5]' : 'border-[#7B2C3B]'}`}>
              <span className="text-[9px] font-medium text-[#2A2A2A] text-center leading-tight">{t.label}<br /><span className="text-[8px] text-[#6A6A6A]">{t.filled}/{t.seats}</span></span>
            </div>
          </div>
        ))}
        <p className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[8px] italic text-[#9A8A7E]">— dance floor —</p>
      </div>
    </div>
  );
}

// ---------- 7. Budget ----------
export function MockBudget() {
  const cats = [
    { name: 'Venue', pct: 78, color: WINE },
    { name: 'Catering', pct: 62, color: OCHRE },
    { name: 'Photography', pct: 100, color: SAGE },
    { name: 'Flowers &amp; decor', pct: 42, color: '#B37474' },
    { name: 'Attire', pct: 88, color: '#9A6B7E' },
  ];
  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-serif text-base text-[#2A2A2A]">Smart budget</p>
        <span className="text-[9px] text-[#7B2C3B] bg-[#F4E4DA] px-2 py-0.5 rounded-full">$11,550 left of $28,000</span>
      </div>
      <div className="rounded-lg border border-[#EFE3D8] bg-white p-3">
        <div className="flex items-center justify-between text-[9px] text-[#6A6A6A]">
          <span>Total spent</span><span className="text-[#2A2A2A] font-medium">$16,450 (59%)</span>
        </div>
        <div className="mt-1 h-2 rounded-full bg-[#F4E4DA] overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#7B2C3B] to-[#B8896C]" style={{ width: '59%' }} />
        </div>
      </div>
      <div className="rounded-lg border border-[#EFE3D8] bg-white p-3 space-y-1.5">
        {cats.map((c) => (
          <div key={c.name} className="grid grid-cols-[80px_1fr_35px] items-center gap-2 text-[9px]">
            <span className="text-[#3A3A3A] truncate" dangerouslySetInnerHTML={{ __html: c.name }} />
            <div className="h-1.5 rounded-full bg-[#F4E4DA] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${c.pct}%`, background: c.color }} />
            </div>
            <span className={`font-medium ${c.pct >= 95 ? 'text-[#7B2C3B]' : 'text-[#6A6A6A]'}`}>{c.pct}%</span>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-[#F4C1C1] bg-[#FDECEC] p-2 flex items-start gap-2">
        <div className="text-[#7B2C3B] mt-0.5"><Star className="w-3 h-3" /></div>
        <p className="text-[8px] text-[#4A2020] leading-snug"><strong>Photography</strong> is 100% used — logging any more spend will trip a live alert.</p>
      </div>
    </div>
  );
}
